import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const variantClass: Record<Variant, string> = {
  primary:
    "bg-accent-500 text-white shadow-md shadow-accent-500/20 hover:bg-accent-600 hover:shadow-lg hover:shadow-accent-500/30",
  secondary:
    "bg-white text-navy-800 ring-1 ring-navy-200 hover:ring-navy-400 hover:bg-navy-50",
  ghost: "text-navy-700 hover:bg-navy-50 hover:text-navy-900",
  danger:
    "bg-red-500 text-white shadow-md shadow-red-500/20 hover:bg-red-600",
};

const sizeClass: Record<Size, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2.5 text-sm",
  lg: "px-6 py-3.5 text-base",
};

type BaseProps = {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
  fullWidth?: boolean;
};

type ButtonProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
    href?: undefined;
  };

type LinkProps = BaseProps & {
  href: string;
  external?: boolean;
};

export function Button(props: ButtonProps | LinkProps) {
  const {
    variant = "primary",
    size = "md",
    fullWidth = false,
    className = "",
    children,
  } = props;

  const baseCls = "inline-flex items-center justify-center gap-1.5 rounded-full font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60";
  const cls = `${baseCls} ${variantClass[variant]} ${sizeClass[size]} ${fullWidth ? "w-full" : ""} ${className}`.trim();

  if ("href" in props && props.href) {
    if ("external" in props && props.external) {
      return (
        <a href={props.href} className={cls} target="_blank" rel="noopener">
          {children}
        </a>
      );
    }
    return (
      <Link to={props.href} className={cls}>
        {children}
      </Link>
    );
  }

  const { variant: _v, size: _s, fullWidth: _f, className: _c, children: _ch, href: _h, ...rest } = props as ButtonProps;
  void _v; void _s; void _f; void _c; void _ch; void _h;
  return (
    <button className={cls} {...rest}>
      {children}
    </button>
  );
}
